<script setup lang="ts">
import { computed, ref } from 'vue'
import { Music2, Snowflake, Star } from 'lucide-vue-next'
import type { AdventureSkin } from '@/ui/pip-skins'
import type { StreamHudData } from '@/ui/stream-hud'
import type { AppLocale } from '@/i18n/messages'
import { isIdleMotionActive, useAdventureMotion } from '@/ui/adventure-motion'
import AdventureMetric from './AdventureMetric.vue'
import AdventureProgress from './AdventureProgress.vue'
import StreamCompanion from './StreamCompanion.vue'

const props = defineProps<{ skin: AdventureSkin; data: StreamHudData; locale: AppLocale; preview?: boolean }>()
const root = ref<HTMLElement>()
const feedback = useAdventureMotion(root, () => ({ data: props.data, skin: props.skin, preview: props.preview }))
const running = computed(() => props.data.status === 'running')
const motionActive = computed(() => isIdleMotionActive(props.data.status))
</script>

<template>
  <div ref="root" class="adventure-composition" :data-feedback="feedback" :data-motion-active="motionActive" data-stream-measure>
    <div v-if="skin === 'stream-camp'" class="snow-expedition" data-stream-measure>
      <div class="snow-party" data-tier="3" aria-hidden="true">
        <svg class="snow-peaks" viewBox="0 0 100 90"><path d="M0 80 32 9 66 80Z" fill="#83b7d8" /><path d="m18 40 14-31 15 31-15-8Z" fill="#edfaff" /><path d="m35 80 35-53 30 53Z" fill="#bbdfed" /><path d="m55 50 15-23 14 23-14-5Z" fill="#f5fcff" /></svg>
        <StreamCompanion class="snow-companion" name="yeti" :active="motionActive" :preview="preview" />
        <span class="snow-drift" />
      </div>
      <section class="snow-display" data-stream-measure>
        <div class="snow-heading" data-tier="1" data-stream-measure><span class="adventure-level" data-stream-measure>Lv. <b>{{ data.level }}</b></span><span data-feedback-accent aria-hidden="true"><Snowflake /></span></div>
        <AdventureMetric kind="exp" :data="data" :locale="locale" />
        <AdventureProgress variant="ice" :data="data" :locale="locale" />
      </section>
      <aside v-if="data.enabled.mesos" class="snow-supplies" data-tier="2" data-stream-measure>
        <AdventureMetric kind="mesos" :data="data" :locale="locale" />
      </aside>
    </div>

    <div v-else-if="skin === 'stream-clockwork'" class="toy-station" data-stream-measure>
      <section class="toy-display" data-stream-measure>
        <div class="toy-studs" data-tier="5" aria-hidden="true"><i /><i /><i /><i /></div>
        <div class="toy-heading" data-tier="1" data-stream-measure><span class="adventure-level" data-stream-measure>Lv. <b>{{ data.level }}</b></span><span data-feedback-accent aria-hidden="true"><Star /></span></div>
        <AdventureMetric kind="exp" :data="data" :locale="locale" />
        <AdventureProgress variant="blocks" :data="data" :locale="locale" />
      </section>
      <div class="toy-platform" data-tier="3" aria-hidden="true"><StreamCompanion class="toy-companion" name="teddy" :active="motionActive" :preview="preview" /><div class="toy-stack"><i /><i /><i /></div></div>
      <aside v-if="data.enabled.mesos" class="toy-cargo" data-tier="2" data-stream-measure><AdventureMetric kind="mesos" :data="data" :locale="locale" /></aside>
    </div>

    <div v-else class="bean-studio" :data-running="running" data-stream-measure>
      <div class="bean-deck" data-tier="3" aria-hidden="true">
        <span class="bean-station-mark"><Music2 /> PB / 01</span>
        <div class="bean-turntable">
          <span class="bean-record"><i /></span>
          <StreamCompanion class="bean-host" name="pinkbean" :active="motionActive" :preview="preview" />
          <svg class="bean-tonearm" viewBox="0 0 40 80"><circle cx="29" cy="9" r="6" /><path d="M29 9v36L13 64" /><path d="m9 61 8 6-5 8-8-6Z" /></svg>
        </div>
        <span class="bean-deck-lights"><i /><i /><i /></span>
      </div>
      <section class="bean-display" data-stream-measure>
        <div class="bean-heading" data-tier="1" data-stream-measure><span class="adventure-level" data-stream-measure>Lv. <b>{{ data.level }}</b></span><Music2 data-feedback-accent aria-hidden="true" /></div>
        <AdventureMetric kind="exp" :data="data" :locale="locale" />
        <AdventureProgress variant="tuner" :data="data" :locale="locale" />
      </section>
      <aside v-if="data.enabled.mesos" class="bean-channel" data-tier="2" data-stream-measure><AdventureMetric kind="mesos" :data="data" :locale="locale" /></aside>
    </div>

    <span class="adventure-celebration" data-tier="3" aria-hidden="true">
      <span v-for="particle in 6" :key="particle" data-feedback-particle><Snowflake v-if="skin === 'stream-camp'" /><Star v-else-if="skin === 'stream-clockwork'" /><Music2 v-else /></span>
    </span>
  </div>
</template>
